import * as React from 'react';
import { useState, useEffect } from 'react';
import { RequestCard, RequestType } from '../Types/RequestCard';
import { CopilotRequestStatusBadge } from '../Common/RequestStatusBadge';
import { htmlIf } from '../Utils/HTML';
import { offerCountLabel } from '../Utils/ApplicationMode';

// Support both old and new prop formats for backwards compatibility
// Old format: { requests: RequestCard[] } - used by open requests pages
// New format: { activeRequests: RequestCard[], completedRequests: RequestCard[] } - used by my requests page
type Props =
  { activeRequests?: RequestCard[]
  , completedRequests?: RequestCard[]
  , requests?: RequestCard[]  // Legacy prop for backwards compatibility
  , defaultSortOption?: SortOption
  }

enum Tab
  { Active = 'active'
  , Completed = 'completed'
  }

function requestDetailsPath(card: RequestCard) {
  switch (card.requestType) {
    case RequestType.PROJECT_REQUEST:
      return `/copilot/requests/p/${card.requestId}`
    case RequestType.HOURLY_CONTRACT:
      return `/copilot/requests/h/${card.requestId}`
  }
}

function formatDate(dateString: string):string {
  const date = new Date(dateString)
  return (
    date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  )
}

enum SortOption
  { RecentlyUpdated = 'recently-updated'
  , NameAsc = 'name-asc'
  , NameDesc = 'name-desc'
  , CreatedAtAsc = 'created-at-asc'
  , CreatedAtDesc = 'created-at-desc'
  , Status = 'status'
  }

const allSortOptions = [
  SortOption.RecentlyUpdated,
  SortOption.Status,
  SortOption.NameAsc,
  SortOption.NameDesc,
  SortOption.CreatedAtAsc,
  SortOption.CreatedAtDesc
]

const copilotOpenRequestsPath = '/copilot/requests/open-requests'

const Requests = (props: Props) => {
  function sortOptionDisplayName(sortOption: SortOption): string {
    switch (sortOption) {
      case SortOption.RecentlyUpdated:
        return "Recently Updated"
      case SortOption.Status:
          return "Status (Open/In Progress/Completed)"
      case SortOption.NameAsc:
        return "Name (A–Z)"
      case SortOption.NameDesc:
        return "Name (Z–A)"
      case SortOption.CreatedAtAsc:
        return "Posting Date (Oldest to Newest)"
      case SortOption.CreatedAtDesc:
        return "Posting Date (Newest to Oldest)"
    }
  }
  const sortRequests = (requests: RequestCard[]): RequestCard[] => {
    switch (sortOption) {
      case SortOption.RecentlyUpdated:
        return requests.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      case SortOption.NameAsc:
        return requests.sort((a, b) => a.title.localeCompare(b.title));
      case SortOption.NameDesc:
        return requests.sort((a, b) => b.title.localeCompare(a.title));
      case SortOption.CreatedAtAsc:
        return requests.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      case SortOption.CreatedAtDesc:
        return requests.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      case SortOption.Status:
        return requests.sort((a, b) => {
          const order = {
            'submitted': 1,
            'declined-pending': 2,
            'claimed': 3,
            'completed': 4,
            'withdrawn': 5,
            'refunded': 6
          };

          return order[a.status] - order[b.status];
        });
      default:
        return requests;
    }
  }

  // Handle backwards compatibility: if legacy 'requests' prop is passed, use it as activeRequests
  // and hide the tabs (since open requests don't have a completed concept)
  const isLegacyMode = props.requests !== undefined;
  const initialActiveRequests = isLegacyMode ? props.requests! : (props.activeRequests ?? []);
  const initialCompletedRequests = isLegacyMode ? [] : (props.completedRequests ?? []);

  const [tab, setTab] = useState(Tab.Active);
  const [sortOption, setSortOption] = useState(props.defaultSortOption ?? SortOption.RecentlyUpdated);
  const [activeRequests, setActiveRequests] = useState(initialActiveRequests);
  const [completedRequests, setCompletedRequests] = useState(initialCompletedRequests);
  const [searchText, setSearchText] = useState('');
  const [filteredRequests, setFilteredRequests] = useState(initialActiveRequests);

  // Get the current requests based on the active tab
  const currentRequests = tab === Tab.Active ? activeRequests : completedRequests;

  // Check if there are any requests at all (for empty state)
  const hasAnyRequests = activeRequests.length > 0 || completedRequests.length > 0;

  // This hook serves to re-sort and re-filter the list of requests any time one of the following occurs:
  // 1) The active tab changes
  // 2) The underlying list of requests changes
  // 3) The sort option is changed
  // 4) The search text is changed
  useEffect(() => {
    const sortedRequests = sortRequests([...currentRequests]);

    const filtered = sortedRequests.filter((request) => {
      const searchFields = [
        request.title,
        request.description,
        request.customerName,
        CopilotRequestStatusBadge({status: request.status, isDirectRequest: (request.copilot?.id > 0), requiredCapabilityDisplayName: request.requiredCapabilityDisplayName}).props.children
      ];
      return (
        searchFields.some((field) =>
          field.toLowerCase().includes(searchText.toLowerCase())
        )
      );
    });

    setFilteredRequests(filtered);
  }, [tab, activeRequests, completedRequests, sortOption, searchText])

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchText(query);
  }

  const onSelectSortOption = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedSortOption = event.target.value as SortOption;
    setSortOption(selectedSortOption);
  }

  const ViewRequestCard = ({request}: {request: RequestCard}) => {
    return (
      <div className="col-md-6 mb-2">
        <div className="card h-100 rounded-4">
          <a href={requestDetailsPath(request)} className="text-decoration-none">
            <div className="card-body p-3">
              <div className="h6 mb-1">{request.title}</div>
              <div className="d-flex align-items-center">
                <div className="fs-sm text-gray-900">{request.customerName}</div>
                {htmlIf(request.isAgentVirtuoso,
                  <div className="ms-1 fs-xs badge small bg-info text-white">Virtuoso</div>
                )}
              </div>
              <div className="fs-xs mt-1">
                Posted on {formatDate(request.createdAt)}
              </div>
              <div className="mt-1">
                <CopilotRequestStatusBadge status={request.status} isDirectRequest={request.copilot?.id > 0} requiredCapabilityDisplayName={request.requiredCapabilityDisplayName}/>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-1">
                <div className="fs-md fw-semibold text-gray-900">{request.copilotRateDescription}</div>
                {htmlIf(request.isAcceptingOffers || request.isApplicationMode,
                  <span className="fs-xs text-primary fw-semibold">{offerCountLabel(request.pendingOfferCount, request.isApplicationMode)}</span>
                )}
              </div>
            </div>
          </a>
        </div>
      </div>
    )
  }

  const ViewEmptyState = () => (
    <div className="my-3 text-center">
      <div className="avatar avatar-md">
        <div className="avatar-title bg-accent rounded text-dark h3">
          <i className="ai-file-text" />
        </div>
      </div>
      <div className="fs-lg text-dark mt-2">You don't have any requests yet — claim one now to get started!</div>
      <a href={copilotOpenRequestsPath} className="btn btn-primary mt-2">Claim a request</a>
    </div>
  )

  const ViewTabEmptyState = () => (
    <div className="my-3 text-center">
      <div className="avatar avatar-md">
        <div className="avatar-title bg-accent rounded text-dark h3">
          <i className="ai-file-text" />
        </div>
      </div>
      <div className="fs-lg text-dark mt-2">
        {tab === Tab.Active
          ? "No active requests"
          : "No completed requests yet"
        }
      </div>
    </div>
  )

  const ViewTabs = () => (
    <ul className="nav nav-tabs border-bottom mt-3">
      {[
        { label: 'Active', value: Tab.Active, count: activeRequests.length },
        { label: 'Completed', value: Tab.Completed, count: completedRequests.length }
      ].map(({ label, value, count }) => (
        <li key={label} className={`nav-item cursor-pointer me-2 ${tab === value ? 'mb-0 border-bottom border-primary' : ''}`}>
          <button
            className={`btn btn-link nav-link rounded-0 border-0 cursor-pointer p-2 ${tab === value ? 'active fw-semibold' : 'fw-normal'}`}
            onClick={() => setTab(value)}
          >
            {label} ({count})
          </button>
        </li>
      ))}
    </ul>
  )

  return (
    <div className="">
      <div className="">
        { !hasAnyRequests
        ? <ViewEmptyState />
        :
          <>
            {!isLegacyMode && <ViewTabs />}
            <div className="row mt-3 align-items-center">
              <div className="col-md-8">
                <div className="input-group align-items-center p-0 bg-white">
                  <div className="input-group-prepend ms-2">
                    <i className="ai-search"></i>
                  </div>
                  <input
                    className="form-control"
                    type="search"
                    value={searchText}
                    onChange={handleSearchInputChange}
                    placeholder="Search by title, description, or client…"
                  />
                </div>
              </div>
              <div className="col-md-4 mt-2 mt-md-0 text-end">
                <select className="form-select bg-white" onChange={onSelectSortOption} value={sortOption}>
                  {allSortOptions.map((option: SortOption) => (
                    <option key={option} value={option}>
                      {sortOptionDisplayName(option)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            { currentRequests.length === 0 && !isLegacyMode
            ? <ViewTabEmptyState />
            : <div className="row mt-3">
                {filteredRequests.map((request) => (
                  <ViewRequestCard
                    request={request}
                    key={`${request.requestType}-${request.requestId}`}
                  />
                ))}
              </div>
            }
          </>
        }
      </div>
    </div>
  )
};


export default Requests;
